import { addDays, format, type Locale } from "date-fns";
import { fromZonedTime, toZonedTime, formatInTimeZone } from "date-fns-tz";
import { useCompany } from "@/query/hooks/useCompany";

export const FALLBACK_TIMEZONE = "Europe/Paris";

const HAS_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:/;

function hasTimeComponent(s: string): boolean {
  return HAS_TIME_RE.test(s);
}

function normalizeToISOWithZ(apiStr: string): string {
  const iso = apiStr.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:)/, "$1T$2");
  // If the string already carries a UTC marker or explicit offset, leave it
  // alone — appending "Z" to "...+00:00" yields an invalid ISO 8601 string
  // that browsers parse inconsistently.
  if (iso.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(iso)) return iso;
  return iso + "Z";
}

function stripZ(iso: string): string {
  // Match the legacy `localDateTimeToApiString` shape: drop the trailing "Z"
  // and any fractional seconds. Python 3.10's `datetime.fromisoformat`
  // rejects 3-digit milliseconds, and the backend convention is "no fraction".
  const noZ = iso.endsWith("Z") ? iso.slice(0, -1) : iso;
  return noZ.replace(/\.\d+$/, "");
}

export function useCompanyTimezone(): string {
  const { data } = useCompany();
  return data?.timezone || FALLBACK_TIMEZONE;
}

/**
 * True once `useCompany()` has resolved with a real timezone. Use this to
 * defer state initialization that anchors on `nowAsZonedDate(tz)` etc., so
 * a `useState` lazy initializer doesn't permanently capture the
 * `FALLBACK_TIMEZONE` for non-Paris tenants.
 */
export function useCompanyTimezoneReady(): boolean {
  const { isSuccess, data } = useCompany();
  return isSuccess && !!data?.timezone;
}

/**
 * True once `useCompany()` has *settled* (success or error), regardless of
 * whether a timezone is set. Unlike `useCompanyTimezoneReady`, this does NOT
 * require a non-empty timezone.
 *
 * Use it to gate data fetching that converts a range via `useCompanyTimezone()`
 * (which already falls back to `FALLBACK_TIMEZONE`): waiting until the company
 * query settles still avoids firing a first request against a transient
 * fallback tz, but a tenant with no timezone configured isn't stuck loading
 * forever (which `useCompanyTimezoneReady` would cause).
 */
export function useCompanyTimezoneSettled(): boolean {
  const { isSuccess, isError } = useCompany();
  return isSuccess || isError;
}

export function apiToZoned(
  apiStr: string | null | undefined,
  tz: string,
): Date | null {
  if (!apiStr) return null;
  if (!hasTimeComponent(apiStr)) {
    const [y, m, d] = apiStr.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }
  const utcDate = new Date(normalizeToISOWithZ(apiStr));
  if (Number.isNaN(utcDate.getTime())) return null;
  return toZonedTime(utcDate, tz);
}

export function zonedToApi(date: Date, time: string, tz: string): string {
  const validTime = /^([01]?\d|2[0-3]):[0-5]\d$/.test(time) ? time : "00:00";
  const [h, m] = validTime.split(":").map(Number);
  const ymd = format(date, "yyyy-MM-dd");
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const utc = fromZonedTime(`${ymd}T${hh}:${mm}:00`, tz);
  return stripZ(utc.toISOString());
}

export function extractZonedDateStr(
  apiStr: string | null | undefined,
  tz: string,
): string | null {
  if (!apiStr) return null;
  if (!hasTimeComponent(apiStr)) return apiStr.slice(0, 10);
  const utc = new Date(normalizeToISOWithZ(apiStr));
  if (Number.isNaN(utc.getTime())) return apiStr.slice(0, 10);
  return formatInTimeZone(utc, tz, "yyyy-MM-dd");
}

export function extractZonedTimeStr(
  apiStr: string | null | undefined,
  tz: string,
): string {
  if (!apiStr || !hasTimeComponent(apiStr)) return "00:00";
  const utc = new Date(normalizeToISOWithZ(apiStr));
  if (Number.isNaN(utc.getTime())) return "00:00";
  return formatInTimeZone(utc, tz, "HH:mm");
}

/**
 * Extract the company-tz-local calendar date from a UTC+0 datetime string,
 * returned as a Date whose browser-local fields (getFullYear/Month/Date)
 * equal the company-tz calendar day. Time fields are zeroed.
 *
 * E.g. "2026-03-17T05:00:00" in `America/Los_Angeles` (PDT, UTC-7) →
 * Date for Mar 16 00:00 (browser-local), since 05:00 UTC = 22:00 Mar 16 PDT.
 *
 * Falls back to "now" on null/empty/invalid input — match the lenient
 * upstream behaviour callers depend on.
 */
export function parseApiDateToLocalDate(dateStr: string, tz: string): Date {
  const d = apiToZoned(dateStr, tz) ?? new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export interface UTCRange {
  startUTC: string;
  endUTC?: string;
}

function zonedSrcToUTCNoZ(src: string, tz: string): string {
  const utc = fromZonedTime(src, tz);
  if (Number.isNaN(utc.getTime())) {
    throw new TypeError(
      `computeUTCRangeInTz: invalid datetime "${src}" in tz "${tz}"`,
    );
  }
  return stripZ(utc.toISOString());
}

export function computeUTCRangeInTz(
  dateStr: string,
  tz: string,
  start?: string,
  end?: string,
  openEnded?: boolean,
): UTCRange {
  if (openEnded) {
    const startSrc = start || `${dateStr}T00:00:00`;
    return { startUTC: zonedSrcToUTCNoZ(startSrc, tz) };
  }
  if (start && end) {
    return {
      startUTC: zonedSrcToUTCNoZ(start, tz),
      endUTC: zonedSrcToUTCNoZ(end, tz),
    };
  }
  const dayStartLocal = `${dateStr}T00:00:00`;
  const [y, m, d] = dateStr.split("-").map(Number);
  const nextDayLocal =
    y && m && d
      ? format(addDays(new Date(y, m - 1, d), 1), "yyyy-MM-dd") + "T00:00:00"
      : `${dateStr}T00:00:00`;
  return {
    startUTC: zonedSrcToUTCNoZ(dayStartLocal, tz),
    endUTC: zonedSrcToUTCNoZ(nextDayLocal, tz),
  };
}

export function formatZoned(
  apiStr: string | null | undefined,
  tz: string,
  pattern: string,
  options?: { locale?: Locale },
): string {
  if (!apiStr) return "";
  if (!hasTimeComponent(apiStr)) {
    const [y, m, d] = apiStr.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return "";
    return format(new Date(y, m - 1, d), pattern, options);
  }
  const utc = new Date(normalizeToISOWithZ(apiStr));
  if (Number.isNaN(utc.getTime())) return "";
  return formatInTimeZone(utc, tz, pattern, options);
}

export function nowAsZonedDate(tz: string): Date {
  return toZonedTime(new Date(), tz);
}

/**
 * Convert a Date whose calendar fields (getFullYear/Month/Date) are meant to
 * be read in `tz`, plus a wall-clock "HH:mm", into a UTC ISO instant string
 * with the "Z" suffix — the exact shape `.toISOString()` produces. Use this
 * for API range filters that take ISO datetimes (e.g.
 * `created_at_s/created_at_e`) where the wall-clock boundary should be the
 * company's, not the browser's.
 */
export function zonedFieldsToUTCISOString(
  date: Date,
  time: string,
  tz: string,
): string {
  return zonedToApi(date, time, tz) + "Z";
}

/**
 * Like `zonedFieldsToUTCISOString` but uses the Date's full year/month/date
 * AND hours/minutes as the wall-clock fields (interpreted in `tz`). Use when
 * the input Date already carries the intended time-of-day — e.g. a
 * "now"-style Date, an end-of-day Date, or a date-time picker.
 */
export function zonedDateToUTCISOString(date: Date, tz: string): string {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return zonedToApi(date, `${hh}:${mm}`, tz) + "Z";
}

/**
 * Pin a calendar Date (year/month/day read in `tz`) to the last representable
 * millisecond of that company-tz day, returned as a UTC ISO instant.
 *
 * Use for inclusive range upper bounds (e.g. `created_at_e` filters): backend
 * filters split between `<` (orders) and `<=` (flows, transfers), and
 * `T23:59:59.999` is unambiguous for both — `<=` catches the full day and
 * `<` doesn't bleed into the next day.
 */
export function endOfDayZonedFieldsToUTCISOString(
  date: Date,
  tz: string,
): string {
  const ymd = format(date, "yyyy-MM-dd");
  const utc = fromZonedTime(`${ymd}T23:59:59.999`, tz);
  return utc.toISOString();
}
